/* eslint-disable react-hooks/rules-of-hooks */
import Cookies from 'js-cookie';
import { removeAuthentication } from '../auth-helper';
import { API_PATH } from '../constants/api';
import { fetchApi, refreshApi } from '../http-request';
import {
  TAdditionalConfig,
  TCookies,
  TParamsChangePassword,
  TParamsForgotChangePassword,
  TParamsLogin,
  TParamsRegister,
  TParamsRegisterVendor,
} from '../types/api/auth';
import { cleanEmptyKeyObject } from '../utils';

let isCallingRefreshToken = false;

const getCookies = async (): Promise<TCookies> => {
  return new Promise((resolve) => {
    const token = Cookies.get('authToken');
    const userRoleId = Cookies.get('userRoleId');
    const customerId = Cookies.get('customerId');
    resolve({ token, userRoleId, customerId });
  });
};

export const authenticationAPI = (additionalConfig?: TAdditionalConfig) => {
  const logout = async (payload: null) => {
    const token = Cookies.get('authToken');
    try {
      const response = await fetchApi.post(API_PATH.auth.logout, payload, {
        withRequestId: true,
        headers: {
          'X-Authorization': `Bearer ${token}`,
        },
      });
      const data = response?.data;
      if ([200, 201].indexOf(data.statusCode) === -1) {
        throw new Error(data.message);
      }

      Cookies.set('userLogout', 'true', {
        domain: additionalConfig?.domainApp,
      });

      return data?.data || null;
    } catch (error: unknown) {
      return Promise.reject(error);
    }
  };

  const refreshToken = async () => {
    const refreshToken = Cookies.get('refreshToken');
    const token = Cookies.get('authToken');
    if (isCallingRefreshToken) {
      return;
    }
    isCallingRefreshToken = true;
    try {
      const response = await refreshApi.post(
        API_PATH.auth.refreshToken,
        {
          authToken: token,
          refreshToken,
        },
        {
          headers: {
            'X-Authorization': `Bearer ${token}`,
          },
        }
      );
      isCallingRefreshToken = false;
      const data = response?.data;
      if ([200, 201].indexOf(data.statusCode) === -1) {
        throw new Error(data.message);
      }
      return data?.data || null;
    } catch (error: unknown) {
      isCallingRefreshToken = false;
      removeAuthentication(additionalConfig?.domainApp);
      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      let authUrl = `${
        additionalConfig?.authUrl || '/'
      }?redirect=${encodeURIComponent(currentUrl)}`;

      if (Cookies.get('userLogout') === 'true') {
        authUrl = additionalConfig?.authUrl || '/';
      }
      window.location.href = authUrl;

      return Promise.reject(error);
    }
  };

  return {
    logout,
    refreshToken,
  };
};

export const login = async (payload: TParamsLogin) => {
  try {
    const response = await fetchApi.post(API_PATH.auth.login, payload, {
      withRequestId: true,
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const verifyCaptcha = async (captchaToken: string) => {
  try {
    const response = await fetchApi.post(
      API_PATH.auth.validateCaptcha,
      {
        gCaptchaResponseToken: captchaToken,
      },
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const sendEmailForgotPassword = async (
  email: string,
  token?: string
) => {
  try {
    const response = await fetchApi.post(
      API_PATH.auth.forgotPassword,
      {
        email,
        token,
      },
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const forgotChangePassword = async (
  params: TParamsForgotChangePassword
) => {
  try {
    const response = await fetchApi.put(
      API_PATH.auth.forgotPasswordChanged,
      {
        newPassword: params.newPassword,
        gCaptchaResponseToken: params.gCaptchaResponseToken,
      },
      {
        headers: {
          'X-Api-Key': params.token,
        },
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const validateTokenForgotPassword = async (token: string) => {
  try {
    const response = await fetchApi.post(
      API_PATH.auth.forgotPasswordValidate,
      null,
      {
        headers: {
          'X-Api-Key': token,
        },
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const register = async (payload: TParamsRegister) => {
  try {
    const cleanPayload = cleanEmptyKeyObject(payload);
    const response = await fetchApi.post(API_PATH.auth.register, cleanPayload, {
      withRequestId: true,
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const resendEmailVerificationRegister = async ({
  apiKey,
  token,
}: {
  apiKey?: string;
  token?: string;
}) => {
  try {
    const response = await fetchApi.post(
      API_PATH.auth.resendEmailVerification,
      {
        token,
      },
      {
        headers: {
          'X-Api-Key': apiKey,
        },
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const resendVerificationUsingCredentials = async ({
  email,
  password,
}: TParamsLogin) => {
  try {
    const response = await fetchApi.post(
      `${API_PATH.auth.resendEmailVerification}/`,
      { email, password },
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const emailVerificationRegister = async (token: string) => {
  try {
    const response = await fetchApi.post(API_PATH.auth.verification, null, {
      headers: {
        'X-Api-Key': token,
      },
      withRequestId: true,
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const registerVendor = async (payload: TParamsRegisterVendor) => {
  try {
    const cleanPayload = cleanEmptyKeyObject(payload);
    const response = await fetchApi.post(
      API_PATH.auth.registerVendor,
      cleanPayload,
      {
        withRequestId: true,
        headers: {
          'X-Api-Key': payload.token,
        },
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const initAuth = async () => {
  const { token, userRoleId, customerId } = await getCookies();

  const url =
    !!userRoleId && !!customerId
      ? `${API_PATH.auth.initAuthorization}?urid=${userRoleId}&cid=${customerId}`
      : API_PATH.auth.initAuthorization;

  try {
    const response = await fetchApi(url, {
      headers: {
        'X-Authorization': `Bearer ${token}`,
      },
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const userProfile = async () => {
  const token = Cookies.get('authToken');
  try {
    const response = await fetchApi(API_PATH.auth.userProfile, {
      headers: {
        'X-Authorization': `Bearer ${token}`,
      },
    });
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const changePassword = async (payload: TParamsChangePassword) => {
  try {
    const response = await fetchApi.post(
      API_PATH.auth.changePassword,
      payload,
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const checkSubscriptionMobi = async () => {
  try {
    const response = await fetchApi.post(
      API_PATH.mobi.order,
      {},
      {
        withRequestId: true,
      }
    );
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const checkSubscriptionStatus = async () => {
  try {
    const response = await fetchApi(API_PATH.auth.subscriptionStatus);
    const data = response?.data;
    if ([200, 201].indexOf(data.statusCode) === -1) {
      throw new Error(data.message);
    }
    return data?.data || null;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};
